import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../cx.js';
import { Button } from './Button.js';
import { CopyField } from './CopyField.js';
import { OtpField } from './OtpField.js';

export interface TestiTotpSetup {
  titolo: string;
  passo1: string;
  passo2: string;
  segreto: string;
  segretoAiuto: string;
  codice: string;
  codiceAiuto: string;
  conferma: string;
  inCorso: string;
  copia: string;
  copiato: string;
  qrAlt: string;
}

export const TESTI_TOTP_SETUP: TestiTotpSetup = {
  titolo: 'Attiva il secondo fattore',
  passo1: '1. Apri la tua app di autenticazione (Google Authenticator, Microsoft Authenticator, 1Password…) e inquadra il codice QR.',
  passo2: '2. Scrivi qui il codice a 6 cifre che l’app mostra adesso.',
  segreto: 'Chiave da inserire a mano',
  segretoAiuto: 'Se non puoi inquadrare il QR, inserisci questa chiave nell’app.',
  codice: 'Codice dell’app di autenticazione',
  codiceAiuto: 'Sono 6 cifre e cambiano ogni 30 secondi.',
  conferma: 'Conferma e attiva',
  inCorso: 'Verifica in corso',
  copia: 'Copia',
  copiato: 'Copiata',
  qrAlt: 'Codice QR da inquadrare con l’app di autenticazione',
};

export interface TotpSetupProps {
  qrDataUrl: string;
  segreto: string;
  codice: string;
  onCodice: (codice: string) => void;
  onConferma: () => void;
  loading?: boolean;
  error?: string;
  testi?: Partial<TestiTotpSetup>;
  /** Contenuto extra sotto (es. Alert). */
  children?: ReactNode;
  className?: string;
}

/** Attivazione del TOTP: QR, chiave per chi non può inquadrare, campo per il codice di conferma. */
export function TotpSetup({ qrDataUrl, segreto, codice, onCodice, onConferma, loading, error, testi: testiProp, children, className }: TotpSetupProps) {
  const t = { ...TESTI_TOTP_SETUP, ...testiProp };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onConferma();
      }}
      noValidate
      className={cx('flex flex-col gap-6', className)}
      data-testid="totp-setup"
    >
      <h2 className="t-title-2 inline-flex items-center gap-2">
        <ShieldCheck aria-hidden size={24} strokeWidth={1.75} className="text-accent" />
        {t.titolo}
      </h2>
      <p className="t-body">{t.passo1}</p>
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <img src={qrDataUrl} alt={t.qrAlt} width={192} height={192} className="rounded-md border border-border bg-[#FFFFFF] p-2 shrink-0" />
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <CopyField label={t.segreto} value={segreto} testoCopia={t.copia} testoCopiato={t.copiato} />
          <p className="t-body-small text-ink-muted">{t.segretoAiuto}</p>
        </div>
      </div>
      <p className="t-body">{t.passo2}</p>
      <OtpField label={t.codice} hint={t.codiceAiuto} value={codice} onChange={onCodice} error={error} autoComplete="one-time-code" />
      {children}
      <Button type="submit" variant="primary" loading={loading} loadingText={t.inCorso} className="self-start">
        {t.conferma}
      </Button>
    </form>
  );
}
